import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { ArrowLeft, ArrowRight, Loader2, Sparkles } from "lucide-react";

const categories = ["stress", "trauma", "physical"];
const categoryLabels = {
  stress: "Mind & Stress",
  trauma: "Emotional Patterns",
  physical: "Body Awareness"
};

const categoryDescriptions = {
  stress: "Understanding your mental state and daily stress patterns",
  trauma: "Exploring emotional tendencies and past experiences",
  physical: "Assessing your body's energy and physical wellness"
};

export default function Questionnaire() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [questions, setQuestions] = useState({});
  const [currentCategory, setCurrentCategory] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchQuestions();
  }, []);

  useEffect(() => {
    if (user?.has_completed_assessment) {
      navigate('/maya-manthan');
    }
  }, [user, navigate]);

  const fetchQuestions = async () => {
    try {
      const response = await axios.get(`${API}/questionnaire`);
      setQuestions(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching questions:", error);
      toast.error("Failed to load questionnaire");
      setLoading(false);
    }
  };

  const currentCategoryKey = categories[currentCategory];
  const currentQuestions = questions[currentCategoryKey] || [];
  const currentQ = currentQuestions[currentQuestion];
  
  const totalQuestions = categories.reduce((sum, cat) => sum + (questions[cat]?.length || 0), 0);
  const answeredQuestions = Object.keys(responses).length;
  const progress = totalQuestions > 0 ? (answeredQuestions / totalQuestions) * 100 : 0;

  const handleAnswer = (answer) => {
    if (!currentQ) return;
    
    setResponses(prev => ({
      ...prev,
      [currentQ.id]: {
        question_id: currentQ.id,
        question_text: currentQ.text,
        answer: answer,
        category: currentCategoryKey
      }
    }));
  };

  const goNext = () => {
    if (currentQuestion < currentQuestions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    } else if (currentCategory < categories.length - 1) {
      setCurrentCategory(prev => prev + 1);
      setCurrentQuestion(0);
    }
  };

  const goPrev = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    } else if (currentCategory > 0) {
      setCurrentCategory(prev => prev - 1);
      const prevCatQuestions = questions[categories[currentCategory - 1]] || [];
      setCurrentQuestion(prevCatQuestions.length - 1);
    }
  };

  const isLastQuestion = currentCategory === categories.length - 1 && 
                         currentQuestion === currentQuestions.length - 1;
  const isFirstQuestion = currentCategory === 0 && currentQuestion === 0;
  const hasCurrentAnswer = currentQ && responses[currentQ.id];

  const handleSubmit = async () => {
    if (answeredQuestions < totalQuestions) {
      toast.error("Please answer all questions before submitting");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/assessment`, {
        responses: Object.values(responses)
      });
      
      toast.success("Assessment complete! Discovering your inner patterns...");
      await refreshUser();
      navigate('/maya-manthan');
    } catch (error) {
      console.error("Submit error:", error);
      toast.error(error.response?.data?.detail || "Failed to submit assessment");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0B0F1A] to-[#1A1F3A] flex items-center justify-center">
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <div className="w-16 h-16 border-2 border-sacred-gold/30 border-t-sacred-gold rounded-full animate-spin mx-auto mb-4" />
          <p className="text-sacred-text-secondary font-body">Preparing your assessment...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0B0F1A] to-[#1A1F3A] relative overflow-hidden" data-testid="questionnaire-page">
      {/* Ambient background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-sacred-purple/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-sacred-gold/5 rounded-full blur-3xl" />
      </div>
      
      {/* Progress Circle */}
      <div className="fixed top-6 right-6 z-50">
        <div className="relative w-16 h-16">
          <svg className="w-16 h-16 -rotate-90">
            <circle
              cx="32"
              cy="32"
              r="28"
              fill="none"
              stroke="rgba(255,255,255,0.05)"
              strokeWidth="3"
            />
            <circle
              cx="32"
              cy="32"
              r="28"
              fill="none"
              stroke="url(#goldGradient)"
              strokeWidth="3"
              strokeLinecap="round"
              strokeDasharray="176"
              strokeDashoffset={176 - (176 * progress) / 100}
              className="transition-all duration-500"
            />
            <defs>
              <linearGradient id="goldGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#D4AF37" />
                <stop offset="100%" stopColor="#F5C542" />
              </linearGradient>
            </defs>
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-sm font-medium text-sacred-text-primary font-body">{Math.round(progress)}%</span>
          </div>
        </div>
      </div>

      {/* Back button */}
      <button
        onClick={() => navigate('/')}
        className="fixed top-6 left-6 z-50 p-3 rounded-xl bg-white/5 backdrop-blur-xl border border-white/10 hover:bg-white/10 transition-all duration-300"
        data-testid="back-to-home-btn"
      >
        <ArrowLeft className="w-5 h-5 text-sacred-text-secondary" />
      </button>

      {/* Main Content */}
      <div className="relative min-h-screen flex items-center justify-center px-6 py-24">
        <div className="w-full max-w-2xl">
          {/* Category Label */}
          <motion.div
            key={currentCategoryKey}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-sacred-card-glass backdrop-blur-xl border border-white/10 text-sacred-gold text-sm font-body mb-4">
              <Sparkles className="w-4 h-4" />
              Part {currentCategory + 1} of {categories.length}
            </span>
            <h2 className="font-heading text-3xl md:text-4xl text-sacred-text-primary mb-2">
              {categoryLabels[currentCategoryKey]}
            </h2>
            <p className="text-sacred-text-secondary font-body">
              {categoryDescriptions[currentCategoryKey]}
            </p>
          </motion.div>

          {/* Question Card */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentQ?.id}
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              transition={{ duration: 0.4 }}
              className="bg-[#12182B]/60 backdrop-blur-2xl border border-white/5 rounded-2xl p-8 md:p-10"
              data-testid="question-card"
            >
              {/* Question Number */}
              <div className="flex items-center justify-between mb-6">
                <span className="text-sacred-text-muted text-sm font-body">
                  Question {currentQuestion + 1} of {currentQuestions.length}
                </span>
                <div className="flex gap-1">
                  {currentQuestions.map((_, idx) => (
                    <div
                      key={idx}
                      className={`w-2 h-2 rounded-full transition-all duration-300 ${
                        idx === currentQuestion
                          ? 'bg-sacred-gold w-6'
                          : idx < currentQuestion || responses[currentQuestions[idx]?.id]
                          ? 'bg-sacred-gold/50'
                          : 'bg-white/10'
                      }`}
                    />
                  ))}
                </div>
              </div>

              {/* Question Text */}
              <h3 className="font-heading text-xl md:text-2xl text-sacred-text-primary mb-8 leading-relaxed">
                {currentQ?.text}
              </h3>

              {/* Answer Options */}
              <div className="space-y-3">
                {currentQ?.options?.map((option, idx) => (
                  <motion.button
                    key={idx}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    onClick={() => handleAnswer(option)}
                    className={`w-full text-left p-5 rounded-xl border transition-all duration-300 font-body ${
                      responses[currentQ.id]?.answer === option
                        ? 'bg-gradient-to-r from-sacred-gold/20 to-sacred-gold/5 border-sacred-gold/50 text-sacred-text-primary shadow-gold-glow'
                        : 'bg-white/5 border-white/5 hover:bg-white/10 hover:border-white/10 text-sacred-text-secondary'
                    }`}
                    data-testid={`answer-option-${idx}`}
                  >
                    <span className={`inline-flex items-center justify-center w-7 h-7 rounded-full text-sm mr-4 ${
                      responses[currentQ.id]?.answer === option
                        ? 'bg-sacred-gold text-sacred-text-on-gold font-medium'
                        : 'bg-white/10 text-sacred-text-muted'
                    }`}>
                      {String.fromCharCode(65 + idx)}
                    </span>
                    {option}
                  </motion.button>
                ))}
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-8">
            <Button
              onClick={goPrev}
              disabled={isFirstQuestion}
              variant="ghost"
              className={`flex items-center gap-2 font-body ${
                isFirstQuestion 
                  ? 'opacity-30 cursor-not-allowed' 
                  : 'text-sacred-text-secondary hover:text-sacred-text-primary'
              }`}
              data-testid="prev-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              Previous
            </Button>

            {isLastQuestion ? (
              <Button
                onClick={handleSubmit}
                disabled={!hasCurrentAnswer || submitting}
                className="bg-gradient-to-r from-sacred-gold to-sacred-gold-light text-sacred-text-on-gold font-body font-medium px-8 py-3 rounded-full hover:shadow-gold-glow transition-all duration-300 disabled:opacity-50"
                data-testid="submit-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  'Complete Assessment'
                )}
              </Button>
            ) : (
              <Button
                onClick={goNext}
                disabled={!hasCurrentAnswer}
                className="bg-gradient-to-r from-sacred-gold to-sacred-gold-light text-sacred-text-on-gold font-body font-medium px-8 py-3 rounded-full hover:shadow-gold-glow transition-all duration-300 disabled:opacity-50"
                data-testid="next-btn"
              >
                Continue Journey
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
